/* eslint-disable @typescript-eslint/no-explicit-any */
import { type JSX, useEffect, useMemo, useState } from "react";

import { useAppState, ZustandAppState } from "./app-state-context";
import { Component, type ComponentDefinition } from "./component";
import { EventEmitter } from "./event-emitter";
import { DynamicLayout, PimoDynamicReactLayout } from "./layout";

/**
 * Views are what we refer to as pages in the app i.e DashboardPage, ReportPage, OverviewPage etc
 * @param name the view name
 * @param layout the layout type
 * A View takes a components and pushes it to a components list
 * @returns div with all the components passed to the view layed out according to the layout passed
 */
export class View<
  AppState,
  Layout extends DynamicLayout,
  LayoutState = Layout extends DynamicLayout<infer State, any, any>
    ? State
    : never,
  LayoutEventNames = Layout extends DynamicLayout<any, infer EventNames, any>
    ? EventNames
    : never,
  LayoutEventPayload = Layout extends DynamicLayout<
    any,
    any,
    infer EventPayload
  >
    ? EventPayload
    : never,
  ComponentLayoutProps = Layout extends DynamicLayout<
    any,
    any,
    any,
    infer ComponentLayoutProps
  >
    ? ComponentLayoutProps
    : never,
> extends EventEmitter<LayoutEventNames, LayoutEventPayload> {
  public name: string;
  private components: Component<
    AppState,
    any,
    any,
    any,
    ComponentLayoutProps
  >[] = [];

  private LayoutComponent: PimoDynamicReactLayout<
    LayoutState,
    LayoutEventNames,
    LayoutEventPayload,
    ComponentLayoutProps
  >;

  private layoutStateMapper: (appState: AppState) => LayoutState = () =>
    ({}) as LayoutState;

  private _dynamicBuildFunction?: (state: any, view: View<any, any>) => void;
  private _dynamicBuildMapStateFunction?: (state: AppState) => any;

  constructor(name: string, layout: Layout) {
    super();
    this.name = name;
    this.LayoutComponent = layout.getLayoutComponent();
  }

  public mapLayoutState(mapper: (appState: AppState) => LayoutState) {
    this.layoutStateMapper = mapper;
  }

  public render(): JSX.Element {
    const RenderedView = () => {
      const [componentsInView, setComponentsInView] = useState(this.components);
      const appState = useAppState();

      const layoutState = useAppState(
        this.layoutStateMapper as (state: ZustandAppState) => any
      );

      const dynamicBuildFunction = this._dynamicBuildFunction;
      const dynamicMapFunction = this._dynamicBuildMapStateFunction;

      const dynamicBuildState = useAppState(
        dynamicMapFunction as (state: ZustandAppState) => any
      );

      useEffect(() => {
        if (!dynamicBuildFunction) {
          return;
        }

        this._resetComponents();
        dynamicBuildFunction(dynamicBuildState, this as View<any, any>);
        setComponentsInView([...this.components]);
      }, [dynamicBuildState, dynamicBuildFunction]);

      const components = useMemo(() => {
        const visibleComponents = componentsInView.filter(
          (component) =>
            component.visibilityMapper == null ||
            component.visibilityMapper(appState as never)
        );

        const componentsWithMappedLayoutProps = visibleComponents.map(
          (component) => {
            if (!component.layoutPropsMapper) {
              return component;
            }

            component.layoutProps = component.layoutPropsMapper(
              appState as never
            );
            return component;
          }
        );

        return componentsWithMappedLayoutProps;
      }, [componentsInView, appState]);

      return (
        <this.LayoutComponent
          components={components}
          fireEvent={this.fireEvent.bind(this)}
          {...layoutState}
        />
      );
    };
    return <RenderedView />;
  }

  public addComponent<
    ComponentState,
    ComponentEventName,
    ComponentEventPayload,
  >(
    componentDefinition: ComponentDefinition<
      ComponentState,
      ComponentEventName,
      ComponentEventPayload,
      ComponentLayoutProps
    >
  ): Component<
    AppState,
    ComponentState,
    ComponentEventName,
    ComponentEventPayload,
    ComponentLayoutProps
  > {
    const component = new Component<
      AppState,
      ComponentState,
      ComponentEventName,
      ComponentEventPayload,
      ComponentLayoutProps
    >(componentDefinition);
    this.components.push(component);
    return component;
  }

  /**
   * This function allows you to build components dynamically based on the app state.
   * It takes a function that maps the app state to a dynamic view state and a function
   * that builds the components based on said dynamic view state.
   *
   * The separation of the mapping function and the build function allows for optimized
   * rebuilding of components when the app state changes, avoiding a rebuild for _every_
   * change in the app state, and rather only when portions required for the dynamic view change.
   */
  buildDynamicallyBasedOnAppState<DVS>(
    dynamicBuildMapStateFunction: (state: AppState) => DVS,
    dynamicBuildFunction: (state: DVS, view: this) => void
  ) {
    this._dynamicBuildMapStateFunction = dynamicBuildMapStateFunction;
    this._dynamicBuildFunction = dynamicBuildFunction as unknown as (
      state: AppState,
      view: View<any, any>
    ) => void;
  }

  public setLayout(layout: Layout) {
    this.LayoutComponent = layout.getLayoutComponent();
  }

  private _resetComponents() {
    this.components = [];
  }
}
